import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Wind, Thermometer, Zap, Droplet, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="pt-24 pb-20">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl font-bold mb-6">Onze Diensten</h1>
          <p className="text-xl text-gray-600">
            Specialistisch installatiewerk voor een duurzaam, veilig en comfortabel huis. Kies een categorie voor meer informatie.
          </p>
        </div>

        <div className="space-y-20">
          <ServiceSection
            id="airco"
            title="Airconditioning"
            desc="Een airco is meer dan alleen verkoeling. Het is een lucht-lucht warmtepomp die uw woning efficiënt kan verwarmen in de winter en koelen in de zomer. Wij installeren single-split en multi-split systemen van topmerken. Inclusief luchtzuivering tegen allergenen."
            features={["Verwarmen & Koelen", "Geluidsstil", "Ontvochtigen & Filteren", "WiFi bediening"]}
            icon={<Wind className="h-10 w-10 text-orange-600" />}
            image="https://res.cloudinary.com/dhyp5qe9q/image/upload/v1773604868/episolo/chat-attachments/user-jh73xrq668fpmhwhwhtrjm7vk97z918r-1773604867876.jpg"
            imgPosition="object-[50%_20%]"
            reverse={false}
            linkPath="/diensten/airco"
          />

          <ServiceSection
            id="warmtepomp"
            title="Warmtepompen"
            desc="Bespaar flink op uw gasrekening. Wij adviseren en installeren hybride en all-electric warmtepompen. Een slimme investering die de waarde van uw woning verhoogt en uw CO2-uitstoot verlaagt."
            features={["Hoge subsidie mogelijk", "Waardevermeerdering woning", "Lagere energierekening", "Stil & Efficiënt"]}
            icon={<Thermometer className="h-10 w-10 text-orange-600" />}
            image="https://assets.zyrosite.com/A85E7zplBNSW667B/screenshot_20250829_154544_photos-mnl4lPv5Dxf4MB3l.jpg"
            imgPosition="object-[center_10%]"
            reverse={true}
            linkPath="/diensten/warmtepomp"
          />

          <ServiceSection
            id="zonnepanelen-elektra"
            title="Zonnepanelen & Elektra"
            desc="Wek uw eigen stroom op en zorg voor een veilige basis in huis. Wij verzorgen complete zonnepaneel-installaties, inclusief het uitbreiden of vernieuwen van uw groepenkast en de installatie van slimme laadpalen."
            features={["Zonnepanelen & Omvormers", "Groepenkast vernieuwen", "Laadpalen installatie", "Krachtstroom aanleggen"]}
            icon={<Zap className="h-10 w-10 text-orange-600" />}
            image="https://res.cloudinary.com/dhyp5qe9q/image/upload/v1773604340/episolo/chat-attachments/user-jh73xrq668fpmhwhwhtrjm7vk97z918r-1773604340269.jpg"
            reverse={false}
            linkPath="/diensten/zonnepanelen-elektra"
          />

          <ServiceSection
            id="cv-sanitair"
            title="CV & Sanitair"
            desc="Uw allround specialist voor verwarming en water. Wij plaatsen en onderhouden CV-ketels, leggen vloerverwarming aan en verzorgen al het leiding- en loodgieterswerk voor badkamers en keukens."
            features={["CV-ketel installatie & onderhoud", "Vloerverwarming aanleggen", "Loodgieterswerk", "Badkamer en keuken installaties"]}
            icon={<Droplet className="h-10 w-10 text-orange-600" />}
            image="https://res.cloudinary.com/dhyp5qe9q/image/upload/v1773605654/episolo/chat-attachments/user-jh73xrq668fpmhwhwhtrjm7vk97z918r-1773605654481.jpg"
            reverse={true}
            linkPath="/diensten/cv-sanitair"
          />
        </div>

        <div className="mt-20 text-center">
          <div className="bg-orange-600 text-white rounded-2xl p-10 md:p-16 max-w-4xl mx-auto shadow-2xl">
            <h2 className="text-3xl font-bold mb-4">Advies nodig over uw situatie?</h2>
            <p className="text-orange-100 mb-8 max-w-2xl mx-auto">Elke woning is anders. Wij komen graag langs om de mogelijkheden te bekijken en u een passend voorstel te doen.</p>
            <Button asChild size="lg" className="bg-white text-orange-600 hover:bg-gray-100 font-bold px-8">
              <Link to="/offerte">Plan gratis adviesgesprek</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceSection = ({ id, title, desc, features, icon, image, reverse, imgPosition = "object-center", linkPath }: any) => (
  <div id={id} className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 scroll-mt-32`}>
    <div className="flex-1">
      <div className="bg-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
        {icon}
      </div>
      <h2 className="text-3xl font-bold mb-4">{title}</h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        {desc}
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {(features ??[]).map((feature: string, idx: number) => (
          <li key={idx} className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-orange-600" />
            <span className="font-medium text-gray-800">{feature}</span>
          </li>
        ))}
      </ul>
      <div className="flex gap-4">
        <Button asChild variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
          <Link to={linkPath}>Meer informatie</Link>
        </Button>
        <Button asChild className="bg-gray-900 hover:bg-gray-800 text-white">
          <Link to="/offerte">Offerte aanvragen <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
    </div>
    <div className="flex-1 w-full">
      <Link to={linkPath} className="block">
        <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/5] md:aspect-square group max-w-lg mx-auto lg:max-w-none cursor-pointer">
          <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500 z-10" />
          <img src={image} alt={title} className={`w-full h-full object-cover ${imgPosition} transform group-hover:scale-105 transition-transform duration-700`} />
        </div>
      </Link>
    </div>
  </div>
);

export default Services;
